/**
 * Uma área secundária recolhida atrás de uma linha de resumo (E91.F2, GUS-51 RF03).
 *
 * A pré-análise processada continuava parecendo formulário de entrada: o QA
 * mediu (UXD-03) o início da tabela a ~1500 px do topo, atrás do protocolo em
 * quatro colunas, do cadastro extraído e do upload. Aqui cada uma dessas áreas
 * vira UMA linha — título, resumo e a ação que a abre — e o corpo só é montado
 * quando alguém pede. Nada é escondido para sempre: o que está recolhido diz o
 * que contém.
 *
 * Controlado de fora de propósito: a pendência de kit precisa abrir "Adicionar
 * documentos" a partir de outro lugar da página (RF08), e um estado interno não
 * a alcançaria.
 */
import type { ReactNode } from 'react';

import { Button } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';

const ts = ptBR.exameEletronico.secoes;

export interface SecaoRecolhidaProps {
  /** Sufixo dos `data-testid` e do `id` do corpo: `secao-<id>`. */
  id: string;
  titulo: string;
  /** Uma linha: o que a área contém, legível com ela fechada. */
  resumo: string;
  /** Rótulo da ação que abre — nomeia o que a pessoa vai fazer lá dentro. */
  abrir: string;
  aberta: boolean;
  onAlternar: () => void;
  /** O resumo pede atenção (divergência, ausência): tom de aviso na linha. */
  atencao?: boolean;
  children: ReactNode;
}

export function SecaoRecolhida({
  id,
  titulo,
  resumo,
  abrir,
  aberta,
  onAlternar,
  atencao = false,
  children,
}: SecaoRecolhidaProps): JSX.Element {
  const tituloId = `secao-${id}-titulo`;
  const corpoId = `secao-${id}-corpo`;
  return (
    <section
      className="border border-border"
      data-testid={`secao-${id}`}
      data-aberta={aberta}
      aria-labelledby={tituloId}
    >
      <div className="flex flex-wrap items-center justify-between gap-sc-3 px-sc-4 py-sc-3">
        <div className="min-w-0">
          <h2 id={tituloId} className="text-body-md font-semibold text-fg">
            {titulo}
          </h2>
          <p
            className={
              atencao ? 'mt-0.5 text-body-sm text-warning-700' : 'mt-0.5 text-body-sm text-fg-muted'
            }
            data-testid={`secao-${id}-resumo`}
          >
            {resumo}
          </p>
        </div>
        <Button
          type="button"
          variant="secondary"
          aria-expanded={aberta}
          aria-controls={corpoId}
          onClick={onAlternar}
          data-testid={`secao-${id}-alternar`}
        >
          {aberta ? ts.recolher : abrir}
        </Button>
      </div>
      {aberta ? (
        <div id={corpoId} className="border-t border-border p-sc-4">
          {children}
        </div>
      ) : null}
    </section>
  );
}

/** O `id` do título da seção — para o card embutido se rotular por ele. */
export function tituloDaSecao(id: string): string {
  return `secao-${id}-titulo`;
}
